import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { useEffect, useRef, useState } from 'react';

/**
 * Write to the clipboard and report whether it actually succeeded — `navigator.clipboard` can be
 * absent (insecure context / older browser) and `writeText` can reject (permission denied), so
 * callers must never flash "copied" without checking this. Shared by every copy-to-clipboard
 * control in the console (VpnGatewayDetail, RegistryDeviceDetail, VpnEnrollmentPage's header
 * dropdown) so they all behave identically.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  if (!navigator.clipboard) return false;
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** A copyable value with a verified success/failure indicator (never claims "Copied" on a silent failure). */
export function CopyRow({ value, label, display }: { value: string; label: string; display?: string }) {
  const [state, setState] = useState<'idle' | 'copied' | 'error'>('idle');
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );

  const onClick = () => {
    void copyToClipboard(value).then((ok) => {
      if (!mountedRef.current) return;
      setState(ok ? 'copied' : 'error');
      setTimeout(() => {
        if (mountedRef.current) setState('idle');
      }, 2000);
    });
  };

  return (
    <SpaceBetween direction="horizontal" size="xxs" alignItems="center">
      <code>{display ?? value}</code>
      <Button iconName="copy" variant="inline-icon" ariaLabel={`Copy ${label}`} onClick={onClick} />
      {state === 'copied' && (
        <Box fontSize="body-s" color="text-status-success">
          Copied
        </Box>
      )}
      {state === 'error' && (
        <Box fontSize="body-s" color="text-status-error">
          Copy failed
        </Box>
      )}
    </SpaceBetween>
  );
}
